import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";

import { AuthService } from "./authentication.service";
import { CargoService } from "./cargo.service";
import { PermissionRole } from "../resources/permission-role";
import { Permission } from "../resources/permission";
import { SelectGpsService } from "src/app/shared/select-gps/select-gps.service";
import { SettingId } from "../interfaces/saas-config";
import { DateManager } from "../managers/date.manager";
import { Cargo } from "../interfaces/cargo";
import { Vehicle } from "../interfaces/vehicle";

type GpsProviderLite = { monitor: boolean; name: string; companyId: string; id: string };

/**
 * Lógica de activación automática del rastreo satelital (Fase 1: Monitor).
 *
 * Centraliza, para no duplicar entre cargo-options y cargo-tracking:
 * - la decisión de si un cargo debe registrarse automáticamente,
 * - el cálculo del rango de fechas (dateLoad → descargue último destino, máx 5 días),
 * - la llamada al registro (`registerVehicleInMonitor`).
 *
 * Reglas de decisión (`shouldAutoRegister`):
 *  1. Siempre se exige GPS compatible con Monitor.
 *  2. Super permiso (módulo solo-innovbo `satelliteTracking.forceMonitorTracking`): ignora
 *     config de empresa y SaaS.
 *  3. En caso normal: flag SaaS `automaticSatelliteTracking` Y `company.gpsConfiguration.monitor.active`.
 */
@Injectable()
export class SatelliteTrackingService {

  private providersCache: GpsProviderLite[] | null = null;

  constructor(
    private authService: AuthService,
    private cargoService: CargoService,
    private permissionRole: PermissionRole,
    private selectGpsService: SelectGpsService
  ) { }

  /** Super permiso (gated a correos @innovbo dentro de PermissionRole.hasPermission). */
  public hasForcePermission(): boolean {
    return this.permissionRole.hasPermission(
      Permission.satelliteTracking.module,
      Permission.satelliteTracking.forceMonitorTracking
    );
  }

  /** El GPS del vehículo corresponde a un proveedor del catálogo con monitor=true. */
  private isMonitorCompatible(vehicle: Vehicle, providers: GpsProviderLite[]): boolean {
    const gpsType = vehicle && vehicle.gps && vehicle.gps.gpsType;
    if (!gpsType) return false;
    return (providers || []).some(p => !!p.monitor && !!p.name && p.name.toLowerCase() === gpsType.toLowerCase());
  }

  private shouldAutoRegister(cargo: Cargo, vehicle: Vehicle, providers: GpsProviderLite[]): boolean {
    if (!cargo || !cargo.id) return false;
    const compatible = this.isMonitorCompatible(vehicle, providers);
    const force = this.hasForcePermission();
    const saasOn = this.authService.hasCompanySettingEnabled(SettingId.AUTOMATIC_SATELLITE_TRACKING);
    const company = this.authService.getCompany();
    const companyMonitorOn = !!(company && company.gpsConfiguration && company.gpsConfiguration.monitor
      && company.gpsConfiguration.monitor.active);
    // Siempre se exige GPS Monitor; super permiso ignora config; en caso normal: flag SaaS + empresa.
    const should = compatible && (force || (saasOn && companyMonitorOn));
    console.log('[RastreoSatelital] decisión auto-registro', {
      cargoId: cargo.id, compatible, force, saasOn, companyMonitorOn, should
    });
    return should;
  }

  /**
   * Calcula el rango de rastreo:
   * - inicio = fecha estimada de la carga (`cargo.dateLoad`).
   * - fin = fecha de descargue del último destino; si supera 5 días desde el inicio,
   *   se limita a inicio + 5 días.
   */
  public computeTrackingDates(cargo: Cargo): { start: string; end: string } | null {
    const start = cargo && cargo.dateLoad;
    if (!start) return null;

    const destinations = cargo.cargoFeature
      && cargo.cargoFeature.uploadDownload
      && cargo.cargoFeature.uploadDownload.destination;
    const lastDownload = destinations && destinations.length
      ? destinations[destinations.length - 1].downloadDate
      : null;

    const maxEnd = DateManager.dateToString(DateManager.add(start, 5, 'days'));

    let end: string;
    if (!lastDownload) {
      end = maxEnd;
    } else {
      const diffDays = DateManager.dateDiff(lastDownload, undefined, start, undefined, 'days');
      end = diffDays > 5 ? maxEnd : lastDownload;
    }

    // La fecha fin del rango de rastreo debe cubrir el último día completo,
    // no cortarse a medianoche (dateLoad/downloadDate son solo-fecha => 00:00).
    end = DateManager.dateToString(DateManager.setEndOfDay(DateManager.stringToDate(end)));

    return { start, end };
  }

  /** El cargo ya tiene un rastreo Monitor vigente (evita registros duplicados). */
  public isAlreadyTracked(cargo: Cargo): boolean {
    if (!cargo || cargo.gpsMonitoringIntegration !== true || !cargo.monitorHubEndDate) return false;
    // monitorHubEndDate en el futuro => rastreo aún vigente.
    return DateManager.dateDiff(cargo.monitorHubEndDate, undefined, new Date(), undefined, 'minutes') > 0;
  }

  private ensureProviders(): Observable<GpsProviderLite[]> {
    if (this.providersCache) return of(this.providersCache);
    return this.selectGpsService.getGpsProviders().pipe(
      map(list => (list || []).map(p => ({
        monitor: !!p.monitor, name: p.name, companyId: p.companyId, id: p.id
      }))),
      tap(list => this.providersCache = list),
      catchError(() => of([] as GpsProviderLite[]))
    );
  }

  /**
   * Registra el rastreo automático si el cargo cumple las condiciones.
   * @returns Observable<boolean>: `true` si registró; `of(false)` si no aplica.
   *          Si el registro se intenta y falla, propaga el error (el llamador decide el fallback).
   */
  public autoRegisterIfApplicable(cargo: Cargo, vehicle: Vehicle): Observable<boolean> {
    return this.ensureProviders().pipe(
      switchMap(providers => {
        if (!this.shouldAutoRegister(cargo, vehicle, providers)) {
          console.log('[RastreoSatelital] no aplica auto-registro', cargo && cargo.id);
          return of(false);
        }
        const dates = this.computeTrackingDates(cargo);
        if (!dates) {
          console.log('[RastreoSatelital] sin fechas calculables, no se registra', cargo && cargo.id);
          return of(false);
        }
        console.log('[RastreoSatelital] registrando automáticamente', { cargoId: cargo.id, start: dates.start, end: dates.end });
        return this.cargoService.registerVehicleInMonitor(cargo.id, dates.start, dates.end).pipe(
          tap(() => console.log('[RastreoSatelital] registro automático OK', cargo.id)),
          map(() => true)
        );
      })
    );
  }
}
